import { useCallback, useEffect, useRef, useState } from "react";

function useQuery(key, queryFn, options = {}) {
  const { enabled = true, select, onSuccess, onError, onSettled } = options;

  const [state, setState] = useState({
    data: null,
    error: null,
    isLoading: enabled,
    isFetching: false
  });

  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const currentKeyRef = useRef(key);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (!enabled || !mountedRef.current) return;

    const requestKey = JSON.stringify(key);
    currentKeyRef.current = requestKey;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setState((prev) => ({
      ...prev,
      isLoading: prev.data === null,
      isFetching: true,
      error: null
    }));

    try {
      const raw = await queryFn({ signal: abortRef.current.signal });

      // Only update state if this is still the current request
      if (!mountedRef.current || currentKeyRef.current !== requestKey) {
        return;
      }

      const finalData = typeof select === "function" ? select(raw) : raw;

      setState({
        data: finalData,
        error: null,
        isLoading: false,
        isFetching: false
      });

      onSuccess?.(finalData);
      onSettled?.(finalData, null);

      return raw;
    } catch (err) {
      if (err?.name === "AbortError") return;

      // Only update error if this is still the current request
      if (mountedRef.current && currentKeyRef.current === requestKey) {
        setState((prev) => ({
          ...prev,
          error: err,
          isLoading: false,
          isFetching: false
        }));

        onError?.(err);
        onSettled?.(null, err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!enabled) return;
    fetchData();
  }, [enabled, fetchData]); // fetchData already includes key in deps

  const refetch = useCallback(() => fetchData(), [fetchData]);

  return {
    data: state.data,
    error: state.error,
    isLoading: state.isLoading,
    isFetching: state.isFetching,
    refetch
  };
}

export default useQuery;
